import { useState } from "react";
import { Copy, Download, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/money";
import { Btn, Field, StatusPill, inputCls } from "@/components/admin/ui";

const fill = (body, client) =>
  body
    .replaceAll("{{client_name}}", client?.name ?? "")
    .replaceAll("{{organization}}", client?.organization ? `(${client.organization})` : "")
    .replaceAll("{{date}}", new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }));

export const ContractEditor = ({ initial, clients, templates, onSaved }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const locked = ["signed", "void"].includes(f.status);
  const link = f.token ? `${window.location.origin}/c/${f.token}` : "";

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    const client = clients.find((c) => c.id === f.client_id);
    setF((x) => ({ ...x, title: x.title || t.name, body: fill(t.body, client) }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { client_id: f.client_id, title: f.title, body: f.body, event_date: f.event_date || "" };
      const { data } = f.id ? await api.put(`/admin/contracts/${f.id}`, body) : await api.post("/admin/contracts", body);
      setF(data);
      onSaved(data);
      toast.success("Contract saved");
      return data;
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const send = async () => {
    const saved = await save();
    if (!saved) return;
    setBusy(true);
    try {
      await api.post(`/admin/contracts/${saved.id}/send`, { origin_url: window.location.origin });
      const next = { ...saved, status: "sent" };
      setF(next);
      onSaved(next);
      toast.success(`Contract emailed to ${saved.client_email}`);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="contract-editor">
      {f.id && <div className="flex items-center gap-3"><StatusPill status={f.status} />{f.signature?.signed_at && <span className="text-xs text-zinc-400">Signed by {f.signature.typed_name} on {fmtDate(f.signature.signed_at)}</span>}</div>}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Client">
          <select disabled={locked} value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} className={inputCls} data-testid="contract-client-select">
            <option value="">Select a client</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.organization ? ` — ${c.organization}` : ""}</option>)}
          </select>
        </Field>
        <Field label="Start from template">
          <select disabled={locked} value="" onChange={(e) => applyTemplate(e.target.value)} className={inputCls} data-testid="contract-template-select">
            <option value="">Choose a template…</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Contract title"><input disabled={locked} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inputCls} data-testid="contract-title-input" /></Field>
      <Field label="Event / session date (optional — marked pending on your calendar when signed)"><input disabled={locked} type="date" value={f.event_date || ""} onChange={(e) => setF({ ...f, event_date: e.target.value })} className={inputCls} data-testid="contract-event-date-input" /></Field>
      <Field label="Contract text (edit freely for this client)">
        <textarea disabled={locked} rows={16} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className={`${inputCls} font-mono-rc text-xs leading-relaxed`} data-testid="contract-body-input" />
      </Field>
      {f.signature?.image && (
        <div className="rounded-xl bg-white p-4" data-testid="contract-signature-view">
          <img src={f.signature.image} alt="Client signature" className="h-20" />
          <p className="mt-2 text-xs text-zinc-700">{f.signature.typed_name} · {new Date(f.signature.signed_at).toLocaleString()}</p>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {f.status === "signed" && f.token && (
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/portal/contracts/${f.token}/pdf`} className="inline-flex items-center gap-2 rounded-full bg-[#C4832B] px-4 py-2 text-sm text-[#0A0A0C] hover:bg-[#E09838]" data-testid="contract-admin-pdf"><Download className="h-4 w-4" /> Download PDF</a>
        )}
        {link && f.status !== "draft" && (
          <Btn onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied"); }} data-testid="contract-copy-link"><Copy className="h-4 w-4" /> Copy client link</Btn>
        )}
        {!locked && <Btn onClick={save} disabled={busy || !f.client_id || !f.title || !f.body} data-testid="contract-save-btn">Save draft</Btn>}
        {!locked && <Btn variant="primary" onClick={send} disabled={busy || !f.client_id || !f.title || !f.body} data-testid="contract-send-btn"><Send className="h-4 w-4" /> {f.status === "draft" || !f.id ? "Save & send" : "Resend"}</Btn>}
      </div>
    </div>
  );
};
